import React, { useState, useEffect, useRef } from 'react';
import { UserProfile } from '../types';
import { X, Save, User, Mail, Phone, MapPin, ShieldCheck, CheckCircle2, Briefcase, Calendar, Flag, Hash, Copy, Camera } from 'lucide-react';

interface UserProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: UserProfile;
  onSave: (updatedUser: UserProfile) => void;
}

export const UserProfileModal: React.FC<UserProfileModalProps> = ({ isOpen, onClose, user, onSave }) => {
  const [formData, setFormData] = useState<UserProfile>(user);
  const [isSaving, setIsSaving] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setFormData(user);
  }, [user, isOpen]);

  if (!isOpen) return null;

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    // Simulate API call
    setTimeout(() => {
      onSave(formData);
      setIsSaving(false);
      onClose();
    }, 800);
  };

  const copyAddress = () => {
    setFormData({ ...formData, correspondenceAddress: formData.address });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({ ...formData, avatar: reader.result as string });
      };
      reader.readAsDataURL(file);
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4 animate-fade-in">
      <div className="bg-white w-full max-w-2xl rounded-2xl shadow-2xl overflow-hidden border border-slate-200 flex flex-col max-h-[90vh]">
        
        {/* Header */}
        <div className="bg-slate-50 border-b border-slate-100 p-6 flex justify-between items-start shrink-0">
          <div className="flex items-center gap-4">
             <div className="relative group cursor-pointer" onClick={triggerFileInput}>
                <div className="w-16 h-16 rounded-full bg-indigo-100 border-2 border-white shadow-md flex items-center justify-center text-indigo-600 text-2xl font-bold overflow-hidden relative">
                    {formData.avatar ? (
                        <img src={formData.avatar} alt="Profile" className="w-full h-full object-cover" />
                    ) : (
                        formData.name.charAt(0)
                    )}
                </div>
                <div className="absolute inset-0 bg-black/40 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <Camera className="text-white" size={20} />
                </div>
                <input 
                   type="file" 
                   ref={fileInputRef} 
                   onChange={handleFileChange} 
                   className="hidden" 
                   accept="image/*"
                />
             </div>
             <div>
                <h2 className="text-xl font-bold text-slate-800">Profile & KYC</h2>
                <div className="flex items-center gap-2 text-sm text-slate-500">
                    <span className="flex items-center gap-1">
                        {formData.isGoogleLinked ? 'Linked Account' : 'Standard Vault ID'}
                    </span>
                    {formData.isVerified && (
                        <span className="bg-emerald-100 text-emerald-700 text-[10px] px-2 py-0.5 rounded-full font-bold flex items-center gap-1">
                            <ShieldCheck size={10} /> KYC Verified
                        </span>
                    )}
                </div>
             </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 hover:bg-slate-100 p-2 rounded-full transition-colors">
            <X size={20} />
          </button>
        </div>

        {/* Scrollable Form Area */}
        <form onSubmit={handleSave} className="p-6 overflow-y-auto custom-scrollbar space-y-8">
           
           {/* Section 1: Personal Identity */}
           <div className="space-y-4">
              <h3 className="text-sm font-bold text-indigo-600 uppercase tracking-wide flex items-center gap-2 border-b border-indigo-50 pb-2">
                 <User size={16} /> Personal Identity Details
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1">
                    <label className="text-xs font-semibold text-slate-500">Full Name (as per PAN/Aadhaar)</label>
                    <input 
                        type="text" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})}
                        className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none text-sm font-medium"
                    />
                </div>
                <div className="space-y-1">
                    <label className="text-xs font-semibold text-slate-500">Date of Birth</label>
                    <div className="relative">
                        <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
                        <input 
                            type="date" value={formData.dob || ''} onChange={(e) => setFormData({...formData, dob: e.target.value})}
                            className="w-full pl-9 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none text-sm font-medium"
                        />
                    </div>
                </div>
                <div className="space-y-1">
                    <label className="text-xs font-semibold text-slate-500">Gender</label>
                    <select 
                        value={formData.gender || ''} onChange={(e) => setFormData({...formData, gender: e.target.value})}
                        className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none text-sm font-medium"
                    >
                        <option value="">Select</option>
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                        <option value="Other">Other</option>
                    </select>
                </div>
                <div className="space-y-1">
                    <label className="text-xs font-semibold text-slate-500">Nationality</label>
                    <div className="relative">
                        <Flag className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
                        <input 
                            type="text" value={formData.nationality || ''} onChange={(e) => setFormData({...formData, nationality: e.target.value})}
                            placeholder="e.g. Indian"
                            className="w-full pl-9 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none text-sm font-medium"
                        />
                    </div>
                </div>
                <div className="space-y-1">
                    <label className="text-xs font-semibold text-slate-500">Marital Status</label>
                    <select 
                        value={formData.maritalStatus || ''} onChange={(e) => setFormData({...formData, maritalStatus: e.target.value})}
                        className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none text-sm font-medium"
                    >
                        <option value="">Select</option>
                        <option value="Single">Single</option>
                        <option value="Married">Married</option>
                        <option value="Divorced">Divorced</option>
                        <option value="Widowed">Widowed</option>
                    </select>
                </div>
              </div>
           </div>

           {/* Section 2: KYC & Legal Identifiers */}
           <div className="space-y-4">
              <h3 className="text-sm font-bold text-indigo-600 uppercase tracking-wide flex items-center gap-2 border-b border-indigo-50 pb-2">
                 <ShieldCheck size={16} /> KYC & Legal Identifiers
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1">
                    <label className="text-xs font-semibold text-slate-500">PAN Number (Last 5 digits)</label>
                    <div className="relative">
                        <Hash className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
                        <input 
                            type="text" maxLength={5} value={formData.panLast5 || ''} onChange={(e) => setFormData({...formData, panLast5: e.target.value.toUpperCase()})}
                            placeholder="XXXXX1234A"
                            className="w-full pl-9 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none text-sm font-medium uppercase"
                        />
                    </div>
                </div>
                <div className="space-y-1">
                    <label className="text-xs font-semibold text-slate-500">Aadhaar (Last 4 digits)</label>
                    <div className="relative">
                         <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-[10px] font-bold">UID</div>
                        <input 
                            type="text" maxLength={4} value={formData.aadhaarLast4 || ''} onChange={(e) => setFormData({...formData, aadhaarLast4: e.target.value})}
                            placeholder="XXXX-XXXX-1234"
                            className="w-full pl-9 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none text-sm font-medium"
                        />
                    </div>
                </div>
                <div className="space-y-1">
                    <label className="text-xs font-semibold text-slate-500">Passport (Optional for NRI)</label>
                    <input 
                        type="text" value={formData.passportNumber || ''} onChange={(e) => setFormData({...formData, passportNumber: e.target.value.toUpperCase()})}
                        className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none text-sm font-medium uppercase"
                    />
                </div>
                <div className="space-y-1">
                    <label className="text-xs font-semibold text-slate-500">Voter ID / Driving License (Optional)</label>
                    <input 
                        type="text" value={formData.voterId || ''} onChange={(e) => setFormData({...formData, voterId: e.target.value.toUpperCase()})}
                        className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none text-sm font-medium uppercase"
                    />
                </div>
              </div>
           </div>

           {/* Section 3: Contact Details */}
           <div className="space-y-4">
              <h3 className="text-sm font-bold text-indigo-600 uppercase tracking-wide flex items-center gap-2 border-b border-indigo-50 pb-2">
                 <Phone size={16} /> Contact Details
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                 <div className="space-y-1">
                    <label className="text-xs font-semibold text-slate-500">Mobile Number (OTP Verified)</label>
                    <input 
                        type="tel" value={formData.phone || ''} onChange={(e) => setFormData({...formData, phone: e.target.value})}
                        className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none text-sm font-medium"
                    />
                 </div>
                 <div className="space-y-1">
                    <label className="text-xs font-semibold text-slate-500">Email Address</label>
                    <input 
                        type="email" value={formData.email} disabled={formData.isGoogleLinked}
                        className={`w-full px-3 py-2 border border-slate-200 rounded-lg outline-none text-sm font-medium ${formData.isGoogleLinked ? 'bg-slate-100 text-slate-500 cursor-not-allowed' : 'bg-slate-50 focus:ring-2 focus:ring-indigo-500'}`}
                    />
                 </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                 <div className="space-y-1">
                    <label className="text-xs font-semibold text-slate-500 flex items-center gap-2">
                        <MapPin size={12} /> Residential Address (Permanent)
                    </label>
                    <textarea 
                        rows={3} value={formData.address || ''} onChange={(e) => setFormData({...formData, address: e.target.value})}
                        className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none text-sm font-medium resize-none"
                    />
                 </div>
                 <div className="space-y-1 relative">
                    <div className="flex justify-between items-center mb-1">
                        <label className="text-xs font-semibold text-slate-500 flex items-center gap-2">
                            <MapPin size={12} /> Correspondence Address
                        </label>
                        <button type="button" onClick={copyAddress} className="text-[10px] text-indigo-600 hover:underline flex items-center gap-1">
                            <Copy size={10} /> Same as Permanent
                        </button>
                    </div>
                    <textarea 
                        rows={3} value={formData.correspondenceAddress || ''} onChange={(e) => setFormData({...formData, correspondenceAddress: e.target.value})}
                        className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none text-sm font-medium resize-none"
                    />
                 </div>
              </div>
           </div>

           {/* Section 4: Financial & Professional */}
           <div className="space-y-4">
              <h3 className="text-sm font-bold text-indigo-600 uppercase tracking-wide flex items-center gap-2 border-b border-indigo-50 pb-2">
                 <Briefcase size={16} /> Financial & Professional
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                 <div className="space-y-1">
                    <label className="text-xs font-semibold text-slate-500">Occupation</label>
                    <select 
                        value={formData.occupation || ''} onChange={(e) => setFormData({...formData, occupation: e.target.value})}
                        className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none text-sm font-medium"
                    >
                        <option value="">Select</option>
                        <option value="Salaried">Salaried</option>
                        <option value="Business">Business</option>
                        <option value="Professional">Professional</option>
                        <option value="Retired">Retired</option>
                        <option value="Student">Student</option>
                        <option value="Homemaker">Homemaker</option>
                    </select>
                 </div>
                 <div className="space-y-1">
                    <label className="text-xs font-semibold text-slate-500">Employer / Business Type</label>
                    <input 
                        type="text" value={formData.employer || ''} onChange={(e) => setFormData({...formData, employer: e.target.value})}
                        placeholder="e.g. Google / Retail"
                        className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none text-sm font-medium"
                    />
                 </div>
                 <div className="space-y-1">
                    <label className="text-xs font-semibold text-slate-500">Annual Income Range</label>
                    <select 
                        value={formData.annualIncomeRange || ''} onChange={(e) => setFormData({...formData, annualIncomeRange: e.target.value})}
                        className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none text-sm font-medium"
                    >
                        <option value="">Select</option>
                        <option value="< 5 Lakhs">Below 5 Lakhs</option>
                        <option value="5-10 Lakhs">5 - 10 Lakhs</option>
                        <option value="10-25 Lakhs">10 - 25 Lakhs</option>
                        <option value="25-50 Lakhs">25 - 50 Lakhs</option>
                        <option value="> 50 Lakhs">Above 50 Lakhs</option>
                    </select>
                 </div>
              </div>
           </div>
        </form>

        {/* Footer */}
        <div className="p-5 border-t border-slate-100 bg-slate-50 flex justify-end gap-3 shrink-0">
            <button 
                type="button"
                onClick={onClose}
                className="px-5 py-2.5 rounded-xl text-slate-600 font-medium hover:bg-slate-200 transition-colors"
            >
                Cancel
            </button>
            <button 
                onClick={handleSave}
                disabled={isSaving}
                className="px-6 py-2.5 rounded-xl bg-slate-900 text-white font-bold shadow-lg shadow-slate-900/20 hover:bg-slate-800 transition-all flex items-center gap-2 disabled:opacity-70"
            >
                {isSaving ? 'Updating...' : <><Save size={18} /> Save Details</>}
            </button>
        </div>
      </div>
    </div>
  );
};